import { PerspectiveCamera, MathUtils } from 'three';
import type { CameraPose } from '../data/types';
import type { Pose } from '../controls/types';
import type { FrameRect } from '../ui/types';
import { CAMERA, WORLD } from '../core/config';

export function poseFromCameraPose(view: CameraPose): Pose {
  const pos = view.position;
  const target = view.target;
  const up = view.up;

  const dx = target[0] - pos[0];
  const dy = target[1] - pos[1];
  const dz = target[2] - pos[2];

  const yaw = Math.atan2(-dx, -dz);
  const horizontalDist = Math.sqrt(dx * dx + dz * dz);
  const pitch = Math.atan2(dy, horizontalDist);

  const upY = up[1];
  const roll = upY < 0.99 ? Math.acos(Math.min(1, upY)) : 0;

  return {
    position: pos,
    yaw,
    pitch,
    roll,
    fovYDeg: view.fovYDeg,
  };
}

export function nearPlaneFor(altitudeAboveGround: number): number {
  const computed = altitudeAboveGround * CAMERA.nearPerMetreAltitude;
  return MathUtils.clamp(computed, CAMERA.nearMin, CAMERA.nearMax);
}

export function painterFrameFit(
  canvasW: number,
  canvasH: number,
  view: CameraPose
): { readonly fovYDeg: number; readonly rect: FrameRect } {
  const paintingAspect = view.aspect;
  const canvasAspect = canvasW / canvasH;

  let width: number;
  let height: number;
  let fovYDeg = view.fovYDeg;

  if (canvasAspect > paintingAspect) {
    height = canvasH;
    width = height * paintingAspect;
  } else {
    width = canvasW;
    height = width / paintingAspect;
    const scaleFactor = canvasH / height;
    fovYDeg = scaledFovYDeg(view.fovYDeg, scaleFactor);
  }

  const x = (canvasW - width) / 2;
  const y = (canvasH - height) / 2;

  return {
    fovYDeg,
    rect: { x, y, width, height },
  };
}

/**
 * Computes FOV scale factor for canvases narrower than the painting aspect.
 * Returns 1.0 for wider canvases, >1.0 for narrower ones.
 */
export function computeFovScale(canvasW: number, canvasH: number, view: CameraPose): number {
  const paintingAspect = view.aspect;
  const canvasAspect = canvasW / canvasH;

  if (canvasAspect >= paintingAspect) {
    return 1.0;
  }

  const width = canvasW;
  const height = width / paintingAspect;
  return canvasH / height;
}

// A taller viewport scales the projection's tangent, not the angle. Multiplying degrees
// directly turned a 60° lens into ~140° on a portrait monitor and could even exceed 180°.
function scaledFovYDeg(fovYDeg: number, scale: number): number {
  return MathUtils.radToDeg(2 * Math.atan(Math.tan(MathUtils.degToRad(fovYDeg) / 2) * scale));
}

/**
 * Legacy applyPose without FOV scaling. Use applyPoseToCamera for new code.
 */
export function applyPose(pose: Pose, camera: PerspectiveCamera, groundHeight: number): void {
  applyPoseToCamera(pose, camera, groundHeight, 1.0);
}

/**
 * Applies pose to camera with optional FOV scaling for narrow canvases.
 * Uses camera.rotation.set to avoid per-frame Euler allocation.
 */
export function applyPoseToCamera(
  pose: Pose,
  camera: PerspectiveCamera,
  groundHeight: number,
  fovScale: number
): void {
  camera.position.set(pose.position[0], pose.position[1], pose.position[2]);
  camera.rotation.set(pose.pitch, pose.yaw, pose.roll, 'YXZ');
  camera.fov = scaledFovYDeg(pose.fovYDeg, fovScale);
  camera.near = nearPlaneFor(pose.position[1] - groundHeight);
  camera.far = WORLD.cameraFar;
  camera.updateProjectionMatrix();
}
